import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-expanded"]
const _hoisted_2 = {
  key: 0,
  class: "notification-panel__badge"
}
const _hoisted_3 = { class: "notification-panel__header" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "notification-panel__state"
}
const _hoisted_6 = {
  key: 1,
  class: "notification-panel__state notification-panel__state--error"
}
const _hoisted_7 = {
  key: 2,
  class: "notification-panel__state"
}
const _hoisted_8 = {
  key: 3,
  class: "notification-panel__list"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  class: "notification-panel__item-icon material-icons",
  "aria-hidden": "true"
}
const _hoisted_11 = { class: "notification-panel__item-content" }
const _hoisted_12 = { class: "notification-panel__item-subject" }
const _hoisted_13 = { class: "notification-panel__item-preview" }
const _hoisted_14 = ["aria-label", "data-tippy-content", "disabled", "onClick"]
const _hoisted_15 = ["disabled", "onClick"]
const _hoisted_16 = { class: "notification-panel__footer" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Timestamp, Unsubscribe } from 'firebase/firestore';
import {
  archiveNotifications,
  getCachedUnreadCount,
  markNotificationRead,
  markNotificationUnread,
  markNotificationsRead,
  subscribeNotificationSummary,
  subscribeRecentNotifications,
  type UserNotification
} from '@/modules/notifications/NotificationClient';
import {
  enableNotificationPush,
  getPushPermissionState,
  isNotificationPushEnabled
} from '@/modules/notifications/PushNotifications';


export default /*@__PURE__*/_defineComponent({
  __name: 'NotificationPanel',
  props: {
    uid: {}
  },
  setup(__props: any) {

const props = __props;
const root = ref<HTMLElement | null>(null);
const popover = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const isLoading = ref(true);
const isMutating = ref(false);
const errorMessage = ref('');
const popoverStyle = ref<Record<string, string>>({});
const unreadCount = ref(getCachedUnreadCount(props.uid));
const notifications = ref<UserNotification[]>([]);
const pushPermission = ref(getPushPermissionState());
const pushEnabled = ref(isNotificationPushEnabled());
const badgeLabel = computed(() => unreadCount.value > 99 ? '99+' : String(unreadCount.value));
const canEnablePush = computed(() => !pushEnabled.value && pushPermission.value !== 'denied' && pushPermission.value !== 'unsupported');
let unsubscribeSummary: Unsubscribe | null = null;
let unsubscribeNotifications: Unsubscribe | null = null;

function positionPopover(): void {
  if (window.innerWidth <= 768) {
    popoverStyle.value = {};
    return;
  }

  const trigger = root.value?.querySelector<HTMLElement>('.notification-panel__trigger');
  if (!trigger) return;
  const triggerRect = trigger.getBoundingClientRect();
  popoverStyle.value = {
    left: `${Math.round(triggerRect.right + 8)}px`,
    bottom: `${Math.round(window.innerHeight - triggerRect.top)}px`
  };
}

function togglePopover(): void {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    void nextTick(positionPopover);
  }
}

function handleViewportChange(): void {
  if (isOpen.value) positionPopover();
}

function stopListeners(): void {
  unsubscribeSummary?.();
  unsubscribeNotifications?.();
  unsubscribeSummary = null;
  unsubscribeNotifications = null;
}

function startListeners(): void {
  stopListeners();
  if (!props.uid) return;
  unreadCount.value = getCachedUnreadCount(props.uid);
  unsubscribeSummary = subscribeNotificationSummary(props.uid, count => {
    unreadCount.value = count;
  }, handleError);
  unsubscribeNotifications = subscribeRecentNotifications(props.uid, items => {
    notifications.value = items;
    isLoading.value = false;
    errorMessage.value = '';
  }, handleError);
}

function handleError(error: Error): void {
  console.error('[Notifications] Listener failed:', error);
  errorMessage.value = 'Notifications are temporarily unavailable.';
  isLoading.value = false;
}

function iconFor(type: UserNotification['type']): string {
  if (type === 'mention') return 'alternate_email';
  if (type === 'assignment' || type === 'approval') return 'assignment_ind';
  if (type === 'shipment_milestone') return 'local_shipping';
  return 'chat_bubble';
}

function formatTime(value: Timestamp | null | undefined): string {
  if (!value?.toDate) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(value.toDate());
}

async function runMutation(action: () => Promise<unknown>): Promise<void> {
  if (isMutating.value) return;
  isMutating.value = true;
  errorMessage.value = '';
  try {
    await action();
  } catch (error) {
    handleError(error instanceof Error ? error : new Error('Notification action failed'));
  } finally {
    isMutating.value = false;
  }
}

function markAllRead(): void {
  void runMutation(() => markNotificationsRead({ scope: 'all' }));
}

function toggleNotificationRead(notification: UserNotification): void {
  void runMutation(() => notification.readAt
    ? markNotificationUnread(notification.id)
    : markNotificationRead(notification.id));
}

function archiveOne(notification: UserNotification): void {
  void runMutation(() => archiveNotifications({ ids: [notification.id] }));
}

function openNotificationSettings(): void {
  window.location.assign('/settings#notifications');
}

function clearRead(): void {
  void runMutation(() => archiveNotifications({ scope: 'read' }));
}

function clearAll(): void {
  void runMutation(() => archiveNotifications({ scope: 'all' }));
}

async function enablePush(): Promise<void> {
  await runMutation(async () => {
    const result = await enableNotificationPush();
    pushPermission.value = getPushPermissionState();
    pushEnabled.value = result === 'enabled';
  });
}

async function openNotification(notification: UserNotification): Promise<void> {
  if (!notification.readAt) {
    unreadCount.value = Math.max(0, unreadCount.value - 1);
    await markNotificationRead(notification.id).catch(handleError);
  }
  window.location.assign(notification.actionPath);
}

function handleDocumentPointerDown(event: PointerEvent): void {
  if (!isOpen.value || !(event.target instanceof Node)) return;
  if (root.value?.contains(event.target) || popover.value?.contains(event.target)) return;
  isOpen.value = false;
}

watch(() => props.uid, startListeners);
onMounted(() => {
  startListeners();
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  window.addEventListener('resize', handleViewportChange);
});
onBeforeUnmount(() => {
  stopListeners();
  document.removeEventListener('pointerdown', handleDocumentPointerDown);
  window.removeEventListener('resize', handleViewportChange);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "notification-panel"
  }, [
    _createElementVNode("button", {
      type: "button",
      class: "notification-panel__trigger",
      "aria-expanded": isOpen.value,
      "aria-label": "Notifications",
      onClick: _withModifiers(togglePopover, ["stop"])
    }, [
      _cache[2] || (_cache[2] = _createElementVNode("i", {
        class: "material-icons",
        "aria-hidden": "true"
      }, "notifications", -1 /* CACHED */)),
      _cache[3] || (_cache[3] = _createElementVNode("span", { class: "notification-panel__mobile-label" }, "Notifications", -1 /* CACHED */)),
      (unreadCount.value > 0)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(badgeLabel.value), 1 /* TEXT */))
        : _createCommentVNode("v-if", true)
    ], 8 /* PROPS */, _hoisted_1),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("section", {
            key: 0,
            ref_key: "popover",
            ref: popover,
            class: "notification-panel__popover",
            style: _normalizeStyle(popoverStyle.value),
            "aria-label": "Recent notifications",
            onPointerdown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
            onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("header", _hoisted_3, [
              _createElementVNode("div", null, [
                _cache[4] || (_cache[4] = _createElementVNode("strong", null, "Notifications", -1 /* CACHED */)),
                _createElementVNode("span", null, _toDisplayString(unreadCount.value) + " unread", 1 /* TEXT */)
              ]),
              _createElementVNode("button", {
                type: "button",
                disabled: unreadCount.value === 0 || isMutating.value,
                onClick: markAllRead
              }, "Mark all read", 8 /* PROPS */, _hoisted_4)
            ]),
            (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, "Loading notifications…"))
              : (errorMessage.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(errorMessage.value), 1 /* TEXT */))
                : (notifications.value.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, "You’re all caught up."))
                  : (_openBlock(), _createElementBlock("ul", _hoisted_8, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notifications.value, (notification) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: notification.id,
                          class: "notification-panel__row"
                        }, [
                          _createElementVNode("button", {
                            type: "button",
                            class: _normalizeClass(["notification-panel__item", { 'notification-panel__item--unread': !notification.readAt }]),
                            onClick: ($event: any) => (openNotification(notification))
                          }, [
                            _createElementVNode("span", _hoisted_10, _toDisplayString(iconFor(notification.type)), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_11, [
                              _createElementVNode("strong", null, _toDisplayString(notification.title), 1 /* TEXT */),
                              _createElementVNode("span", _hoisted_12, _toDisplayString(notification.subject.label || notification.subject.id), 1 /* TEXT */),
                              _createElementVNode("span", _hoisted_13, _toDisplayString(notification.preview), 1 /* TEXT */),
                              _createElementVNode("time", null, _toDisplayString(formatTime(notification.createdAt)), 1 /* TEXT */)
                            ])
                          ], 10 /* CLASS, PROPS */, _hoisted_9),
                          (_openBlock(), _createElementBlock("span", {
                            key: notification.readAt ? 'read-actions' : 'unread-actions',
                            class: "notification-panel__item-actions",
                            "aria-label": "Notification actions"
                          }, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "material-icons",
                              "aria-label": "Notification settings",
                              "data-tippy-content": "Notification settings",
                              "data-tippy-placement": "top",
                              onClick: _withModifiers(openNotificationSettings, ["stop"])
                            }, "tune"),
                            _createElementVNode("button", {
                              type: "button",
                              class: "material-icons",
                              "aria-label": notification.readAt ? 'Mark as unread' : 'Mark as read',
                              "data-tippy-content": notification.readAt ? 'Mark as unread' : 'Mark as read',
                              "data-tippy-placement": "top",
                              disabled: isMutating.value,
                              onClick: _withModifiers(($event: any) => (toggleNotificationRead(notification)), ["stop"])
                            }, _toDisplayString(notification.readAt ?
                  'mark_email_unread' : 'done'), 9 /* TEXT, PROPS */, _hoisted_14),
                            _createElementVNode("button", {
                              type: "button",
                              class: "material-icons",
                              "aria-label": "Archive notification",
                              "data-tippy-content": "Archive",
                              "data-tippy-placement": "top",
                              disabled: isMutating.value,
                              onClick: _withModifiers(($event: any) => (archiveOne(notification)), ["stop"])
                            }, "archive", 8 /* PROPS */, _hoisted_15)
                          ]))
                        ]))
                      }), 128 /* KEYED_FRAGMENT */))
                    ])),
            _createElementVNode("footer", _hoisted_16, [
              (canEnablePush.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    onClick: enablePush
                  }, "Enable alerts"))
                : _createCommentVNode("v-if", true),
              _createElementVNode("button", {
                type: "button",
                disabled: isMutating.value || notifications.value.length === 0,
                onClick: clearRead
              }, "Clear read", 8 /* PROPS */, _hoisted_17),
              _createElementVNode("button", {
                type: "button",
                disabled: isMutating.value || notifications.value.length === 0,
                onClick: clearAll
              }, "Clear all", 8 /* PROPS */, _hoisted_18)
            ])
          ], 36 /* STYLE, NEED_HYDRATION */))
        : _createCommentVNode("v-if", true)
    ]))
  ], 512 /* NEED_PATCH */))
}
}

})